import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Typography,
  Box,
  Chip,
  Card,
  CardContent,
  IconButton,
  Stack,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ImageIcon from "@mui/icons-material/Image";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { QuestionForm } from "@/components/common";
import { uploadMediaFiles } from "@/utils/cloudinaryUpload";

const stripHtml = (html) =>
  (html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const letterFromIndex = (i) => String.fromCharCode(65 + i);

/**
 * Convert one parsed question (from POST /api/admin/pdf/parse) into the local
 * editable shape used by the review dialog.
 */
const toLocalQuestion = (q, defaultPartNumber) => {
  const opts = (q.options || []).slice();
  while (opts.length < 4) {
    opts.push({ value: letterFromIndex(opts.length), text: "", isCorrect: false });
  }
  let options = opts.map((o, i) => ({
    value: o.value || letterFromIndex(i),
    text: o.text || "",
    isCorrect: !!o.isCorrect,
  }));
  // DEV ONLY: default correct answer to "A" when parser didn't detect one,
  // so testers don't have to manually pick a correct answer for every question.
  if (process.env.NODE_ENV !== "production" && !options.some((o) => o.isCorrect)) {
    options = options.map((o, i) => ({ ...o, isCorrect: i === 0 }));
  }
  // Resolve partNumber: explicit on the parsed question (assigned by the BE
  // parser via PART-heading detection + standard TOEIC ranges) takes
  // precedence; otherwise fall back to the admin-selected default when a
  // single PART was chosen.
  const resolvedPart = q.partNumber ?? defaultPartNumber ?? null;

  return {
    _localId:
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `q_${q.number}_${Math.random()}`,
    number: q.number,
    question: q.question || "",
    content: q.passageContent || q.content || "",
    explanation: q.explanation || "",
    partNumber: resolvedPart,
    options,
    imageUrl: q.imageUrl || "",
    audioUrl: q.audioUrl || "",
    extraImageUrl: q.extraImageUrl || "",
    type: "multiple-choice",
    dropped: false,
  };
};

const correctLetterOf = (q) => q.options?.find((o) => o.isCorrect)?.value || null;

/**
 * Convert a local question to QuestionForm's `initialData` shape.
 */
const toFormInitialData = (q) => {
  const correct = correctLetterOf(q);
  return {
    _id: q._localId, // QuestionForm uses _id in its initialData key to detect new data
    question_number: q.number,
    question: q.question || "",
    content: q.content || "",
    explanation: q.explanation || "",
    partNumber: q.partNumber ?? undefined,
    type: "multiple-choice",
    options: q.options.map((o) => o.text || ""),
    correctAnswer: correct ? correct.toLowerCase() : "",
    imagePreview: q.imageUrl || "",
    audioPreview: q.audioUrl || "",
    extraImagePreview: q.extraImageUrl || "",
    image: null,
    audio: null,
    extraImage: null,
    tags: q.tags || [],
  };
};

/**
 * Nested dialog: edits a single question with the same QuestionForm used for
 * manual creation. Handles image upload + rich-text image processing on save.
 */
const EditQuestionDialog = ({
  open,
  question,
  previousQuestion,
  uploadFolder,
  showContent,
  onClose,
  onSave,
}) => {
  const formRef = useRef(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  const initialData = useMemo(
    () => (question ? toFormInitialData(question) : {}),
    [question]
  );

  const previousFormData = useMemo(
    () =>
      previousQuestion
        ? {
            content: previousQuestion.content || "",
            type: "multiple-choice",
            imageUrl: previousQuestion.imageUrl || "",
            audioUrl: previousQuestion.audioUrl || "",
            extraImageUrl: previousQuestion.extraImageUrl || "",
          }
        : null,
    [previousQuestion]
  );

  const handleSave = async () => {
    if (!question) return;
    setSaving(true);
    try {
      // 1. Let QuestionForm finalize rich-text + upload embedded images.
      let processed = formData;
      if (formRef.current?.processImagesAndGetData) {
        processed = await formRef.current.processImagesAndGetData();
      }

      // 2. Upload the standalone media files (image/audio/extraImage), if any.
      const mediaFiles = {
        image: processed.image,
        audio: processed.audio,
        extraImage: processed.extraImage,
      };
      const uploaded = await uploadMediaFiles(mediaFiles, uploadFolder);

      // 3. Validate the correct answer.
      const correctLetter = (processed.correctAnswer || "").toUpperCase();
      if (!correctLetter) {
        toast.error("Please select the correct answer");
        setSaving(false);
        return;
      }

      // 4. Map the form's string-options back to {value,text,isCorrect}.
      const options = (processed.options || []).map((opt, idx) => {
        const value = letterFromIndex(idx);
        const text = typeof opt === "string" ? opt : opt?.text || "";
        return { value, text, isCorrect: value === correctLetter };
      });

      // 5. Build the updated local question.
      //    Media URL resolution priority (per field):
      //      a) freshly uploaded file in this edit  -> uploaded.<x>Url
      //      b) "use previous media" toggle wrote it -> processed.<x>Url
      //      c) existing preview URL on the question -> processed.<x>Preview
      const updated = {
        ...question,
        number: Number(processed.question_number) || question.number,
        question: processed.question || "",
        content: processed.content || "",
        explanation: processed.explanation || "",
        partNumber:
          processed.partNumber !== undefined && processed.partNumber !== ""
            ? Number(processed.partNumber)
            : question.partNumber,
        options,
        imageUrl:
          uploaded.imageUrl ||
          processed.imageUrl ||
          processed.imagePreview ||
          "",
        audioUrl:
          uploaded.audioUrl ||
          processed.audioUrl ||
          processed.audioPreview ||
          "",
        extraImageUrl:
          uploaded.extraImageUrl ||
          processed.extraImageUrl ||
          processed.extraImagePreview ||
          "",
      };

      onSave(updated);
      onClose?.();
    } catch (err) {
      console.error("Failed to save edited question:", err);
      toast.error(err?.message || "Failed to save question");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    if (formRef.current?.resetRichText) {
      formRef.current.resetRichText();
    }
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={saving}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Edit Question {question?.number != null ? `#${question.number}` : ""}
          </Typography>
          <IconButton size="small" onClick={handleClose} disabled={saving}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      {saving && <LinearProgress />}
      <DialogContent dividers>
        {question && (
          <QuestionForm
            ref={formRef}
            key={question._localId}
            initialData={initialData}
            onChange={setFormData}
            previousQuestion={previousFormData}
            showContent={showContent}
            isEditing
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * PdfReviewDialog
 *
 * Shows parsed PDF questions for review. Admin can edit each question via the
 * same QuestionForm used for manual creation, attach media, drop questions,
 * then bulk-submit the kept questions to the target exam / exercise day.
 *
 * Props:
 *  - open / onClose
 *  - parsed:           payload from POST /api/admin/pdf/parse (the `data` field)
 *  - targetLabel:      short description of the destination (e.g. "Exam X")
 *  - sectionDefault:   default `section` for the bulk request
 *  - defaultPartNumber: fallback partNumber for parsed questions missing one
 *  - submitMutation:   RTK Query tuple `[trigger, { isLoading }]`
 *  - buildBulkPayload: ({ section, questions }) => mutation args
 *  - uploadFolder:     Cloudinary folder for per-question media uploads
 *  - showContent:      pass-through to QuestionForm (true for reading section)
 *  - onSubmitted:      callback fired after a successful bulk save
 */
const PdfReviewDialog = ({
  open,
  onClose,
  parsed,
  targetLabel,
  sectionDefault = "reading",
  defaultPartNumber,
  submitMutation,
  buildBulkPayload,
  uploadFolder = "toeic/pdf-import",
  showContent = true,
  onSubmitted,
}) => {
  const [submit, { isLoading: submitting }] =
    submitMutation || [null, { isLoading: false }];
  const [questions, setQuestions] = useState([]);
  const [editingLocalId, setEditingLocalId] = useState(null);

  useEffect(() => {
    setQuestions(
      (parsed?.questions || []).map((q) => toLocalQuestion(q, defaultPartNumber))
    );
    setEditingLocalId(null);
  }, [parsed, defaultPartNumber]);

  const warningsByNumber = useMemo(() => {
    const byNum = {};
    (parsed?.parseErrors || []).forEach((err) => {
      const m = /(?:^|\b)(?:Q(?:uestion)?\s*#?\s*)(\d+)/i.exec(err);
      if (m) {
        const n = Number(m[1]);
        byNum[n] = byNum[n] || [];
        byNum[n].push(err);
      }
    });
    return byNum;
  }, [parsed?.parseErrors]);

  const generalWarnings = useMemo(
    () =>
      (parsed?.parseErrors || []).filter(
        (err) => !/(?:^|\b)(?:Q(?:uestion)?\s*#?\s*)\d+/i.test(err)
      ),
    [parsed?.parseErrors]
  );

  const liveQuestions = useMemo(
    () => questions.filter((q) => !q.dropped),
    [questions]
  );
  const withAnswer = liveQuestions.filter((q) => correctLetterOf(q)).length;

  // Live per-PART distribution of the kept questions. Drives the summary
  // chips at the top of the dialog so the admin can verify (before
  // submitting) which PART buckets will receive each question.
  const partsSummary = useMemo(() => {
    const counts = {};
    let untagged = 0;
    for (const q of liveQuestions) {
      const p = Number(q.partNumber);
      if (Number.isInteger(p) && p >= 1 && p <= 7) {
        counts[p] = (counts[p] || 0) + 1;
      } else {
        untagged += 1;
      }
    }
    const ordered = Object.keys(counts)
      .map((k) => Number(k))
      .sort((a, b) => a - b)
      .map((part) => ({ part, count: counts[part] }));
    return { byPart: ordered, untagged };
  }, [liveQuestions]);

  const editingIndex = useMemo(
    () => questions.findIndex((q) => q._localId === editingLocalId),
    [questions, editingLocalId]
  );
  const editingQuestion = editingIndex >= 0 ? questions[editingIndex] : null;
  const previousForEditing = useMemo(() => {
    if (editingIndex <= 0) return null;
    // Walk backwards to find the nearest non-dropped previous question.
    for (let i = editingIndex - 1; i >= 0; i -= 1) {
      if (!questions[i].dropped) return questions[i];
    }
    return null;
  }, [questions, editingIndex]);

  const updateQuestion = (localId, next) => {
    setQuestions((prev) => prev.map((q) => (q._localId === localId ? next : q)));
  };
  const dropQuestion = (localId) => {
    setQuestions((prev) =>
      prev.map((q) => (q._localId === localId ? { ...q, dropped: true } : q))
    );
  };
  const restoreQuestion = (localId) => {
    setQuestions((prev) =>
      prev.map((q) => (q._localId === localId ? { ...q, dropped: false } : q))
    );
  };

  const handleSubmit = async () => {
    if (!submit || !buildBulkPayload) {
      toast.error("Submit handler not configured");
      return;
    }

    // Validate kept questions.
    for (const q of liveQuestions) {
      const haveCorrect = q.options.some((o) => o.isCorrect);
      const filled = q.options.filter((o) => o.text && o.text.trim()).length;
      if (!haveCorrect) {
        toast.error(`Question #${q.number}: please select a correct answer.`);
        return;
      }
      if (filled < 2) {
        toast.error(`Question #${q.number}: at least 2 options need text.`);
        return;
      }
    }

    const payloadQuestions = liveQuestions.map((q) => ({
      number: q.number,
      question: q.question,
      content: q.content || null,
      explanation: q.explanation || null,
      partNumber: q.partNumber ?? undefined,
      imageUrl: q.imageUrl || null,
      audioUrl: q.audioUrl || null,
      extraImageUrl: q.extraImageUrl || null,
      options: q.options.map((o) => ({
        value: o.value,
        text: o.text || "",
        isCorrect: !!o.isCorrect,
      })),
    }));

    if (payloadQuestions.length === 0) {
      toast.warning("No questions to submit");
      return;
    }

    try {
      const partNumbers = [
        ...new Set(
          payloadQuestions
            .map((q) => Number(q.partNumber))
            .filter((n) => Number.isInteger(n) && n >= 1 && n <= 7)
        ),
      ];
      const args = buildBulkPayload({
        section: sectionDefault,
        questions: payloadQuestions,
        partNumbers,
        parsed,
      });
      const res = await submit(args).unwrap();
      toast.success(res?.message || `${payloadQuestions.length} questions added`);
      onSubmitted?.(res);
      onClose?.();
    } catch (err) {
      console.error("Bulk add failed:", err);
      toast.error(err?.data?.message || err?.message || "Failed to add questions");
    }
  };

  if (!parsed) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>PDF Review</DialogTitle>
        <DialogContent>
          <Alert severity="info">No parsed data to review.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (submitting) return;
          if (reason === "backdropClick") return;
          onClose?.();
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">Review Parsed Questions</Typography>
              {targetLabel && (
                <Typography variant="caption" color="text.secondary">
                  Target: {targetLabel}
                </Typography>
              )}
            </Box>
            <IconButton onClick={onClose} size="small" disabled={submitting}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        {submitting && <LinearProgress />}

        <DialogContent dividers>
          {/* Stats / metadata */}
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2 }}
          >
            {parsed.format && (
              <Chip label={`Format: ${parsed.format}`} color="primary" size="small" />
            )}
            {parsed.part != null && (
              <Chip
                label={`PART ${parsed.part} only`}
                color="secondary"
                size="small"
              />
            )}
            {Array.isArray(parsed.availableParts) &&
              parsed.availableParts.length > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Detected PARTs: ${parsed.availableParts.join(", ")}`}
                />
              )}
            {parsed.extraction?.method && (
              <Chip
                size="small"
                color={parsed.extraction.method === "ocr" ? "warning" : "default"}
                label={
                  parsed.extraction.method === "ocr"
                    ? `Extracted via OCR${
                        parsed.extraction.ocrPages
                          ? ` (${parsed.extraction.ocrPages} page${
                              parsed.extraction.ocrPages === 1 ? "" : "s"
                            })`
                          : ""
                      }`
                    : `Extracted via ${parsed.extraction.method}`
                }
              />
            )}
            <Chip
              size="small"
              variant="outlined"
              color="primary"
              label={`${liveQuestions.length} / ${questions.length} kept`}
            />
            <Chip
              size="small"
              variant="outlined"
              color={withAnswer === liveQuestions.length ? "success" : "warning"}
              label={`${withAnswer} with correct answer`}
            />
            {partsSummary.byPart.map(({ part, count }) => (
              <Chip
                key={`parts-summary-${part}`}
                size="small"
                variant="outlined"
                color="info"
                label={`Part ${part}: ${count}`}
              />
            ))}
            {partsSummary.untagged > 0 && (
              <Chip
                size="small"
                variant="outlined"
                color="error"
                label={`No PART: ${partsSummary.untagged}`}
              />
            )}
          </Stack>

          {generalWarnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Parse warnings ({generalWarnings.length})
              </Typography>
              {generalWarnings.slice(0, 8).map((w, i) => (
                <Typography key={i} variant="caption" display="block">
                  • {w}
                </Typography>
              ))}
              {generalWarnings.length > 8 && (
                <Typography variant="caption" color="text.secondary">
                  …and {generalWarnings.length - 8} more
                </Typography>
              )}
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            Nothing has been saved yet. Review each question, edit if needed,
            then click <strong>Submit</strong> to add them all to the target.
          </Alert>

          {/* Question list */}
          <Stack spacing={1.5}>
            {questions.length === 0 && (
              <Alert severity="info">No questions to review.</Alert>
            )}
            {questions.map((q) => {
              const correct = correctLetterOf(q);
              const qWarnings = warningsByNumber[q.number] || [];
              const hasIssue = !correct || qWarnings.length > 0;
              return (
                <Card
                  key={q._localId}
                  variant="outlined"
                  sx={{
                    opacity: q.dropped ? 0.5 : 1,
                    borderLeft: hasIssue ? "4px solid" : undefined,
                    borderLeftColor: !correct
                      ? "error.main"
                      : qWarnings.length
                      ? "warning.main"
                      : undefined,
                  }}
                >
                  <CardContent sx={{ "&:last-child": { pb: 1.5 } }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      gap={2}
                    >
                      <Box flex={1} minWidth={0}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                          useFlexGap
                          sx={{ mb: 0.5 }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Q{q.number ?? "?"}
                          </Typography>
                          {q.partNumber != null && (
                            <Chip
                              size="small"
                              label={`Part ${q.partNumber}`}
                              variant="outlined"
                            />
                          )}
                          {correct ? (
                            <Chip
                              size="small"
                              color="success"
                              label={`Answer: ${correct}`}
                            />
                          ) : (
                            <Chip
                              size="small"
                              color="error"
                              variant="outlined"
                              label="No answer key"
                            />
                          )}
                          {q.imageUrl && (
                            <Tooltip title="Has image">
                              <ImageIcon fontSize="small" color="action" />
                            </Tooltip>
                          )}
                          {q.audioUrl && (
                            <Tooltip title="Has audio">
                              <AudiotrackIcon fontSize="small" color="action" />
                            </Tooltip>
                          )}
                          {qWarnings.length > 0 && (
                            <Tooltip title={qWarnings.join("\n")}>
                              <WarningAmberIcon fontSize="small" color="warning" />
                            </Tooltip>
                          )}
                          {q.dropped && (
                            <Chip size="small" color="default" label="Dropped" />
                          )}
                        </Stack>

                        {q.content && stripHtml(q.content) && (
                          <Box
                            sx={{
                              p: 1,
                              mb: 1,
                              bgcolor: "grey.50",
                              borderRadius: 1,
                              maxHeight: 110,
                              overflow: "auto",
                              fontSize: 13,
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {stripHtml(q.content).slice(0, 400)}
                            {stripHtml(q.content).length > 400 ? "…" : ""}
                          </Box>
                        )}

                        {q.question && (
                          <Typography variant="body2" sx={{ mb: 0.75 }}>
                            {q.question}
                          </Typography>
                        )}

                        <Stack spacing={0.25} sx={{ pl: 0.5 }}>
                          {q.options.map((c) => (
                            <Typography
                              key={c.value}
                              variant="caption"
                              sx={{
                                color: c.isCorrect ? "success.dark" : "text.secondary",
                                fontWeight: c.isCorrect ? 700 : 400,
                              }}
                            >
                              {c.value}. {c.text || <em>(empty)</em>}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>

                      <Stack direction="row" spacing={0.5} alignItems="flex-start">
                        <Tooltip title="Edit with full form">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => setEditingLocalId(q._localId)}
                              disabled={q.dropped}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {q.dropped ? (
                          <Tooltip title="Restore">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => restoreQuestion(q._localId)}
                            >
                              <RestoreFromTrashIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Drop question">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => dropQuestion(q._localId)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ flex: 1, ml: 1 }}
          >
            {liveQuestions.length} question{liveQuestions.length === 1 ? "" : "s"}{" "}
            will be added.
          </Typography>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || liveQuestions.length === 0}
          >
            {submitting
              ? "Submitting…"
              : `Submit ${liveQuestions.length} question${
                  liveQuestions.length === 1 ? "" : "s"
                }`}
          </Button>
        </DialogActions>
      </Dialog>

      <EditQuestionDialog
        open={!!editingQuestion}
        question={editingQuestion}
        previousQuestion={previousForEditing}
        uploadFolder={uploadFolder}
        showContent={showContent}
        onClose={() => setEditingLocalId(null)}
        onSave={(updated) => updateQuestion(updated._localId, updated)}
      />
    </>
  );
};

export default PdfReviewDialog;
